import React, { useEffect, useMemo, useState } from "react";
import Input from "../../ui/common/Input";
import Button from "../../ui/common/Button";

import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../store";
import { fetchBrands } from "../../../store/slices/brands";
import { generateApiToken } from "../../../store/slices/apiToken";
import { CircleLoader } from "../../../icons/Loaders";
import { RefreshIcon } from "../../../icons/Icons";
import useCheckUserPermissions from "../../../customHooks/useCheckUserPermissions";

import "./UserFormModal.scss";
import { addUser, editUser } from "../../../store/slices/users";

type UserFormModalProps = {
  user: {
    id: string;
    apiTokenAllowed?: boolean;
    active: boolean;
    admin: boolean;
    superUser: boolean;
    email: string;
    firstName: string;
    lastName: string;
    brands: Array<string>;
  };
  setUserModalOpened: Function;
  creatingNewUser: boolean;
};

type FormErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  active?: string;
  admin?: string;
  superUser?: string;
  apiToken?: string;
  apiTokenAllowed?: boolean;
  brands?: string;
  root?: string;
};

const FILTERS = {
  ALL: "all",
  ASSIGNED: "assigned",
  NOT_ASSIGNED: "not_assigned",
};

const UserFormModal = ({ user, setUserModalOpened, creatingNewUser }: UserFormModalProps) => {
  const dispatch = useAppDispatch();
  const brands = useSelector((state: any) => state.brands.data);
  const brandsLoading = useSelector((state: any) => state.brands.isLoading);
  const isPosting = useSelector((state: any) => state.users.isPosting);
  const [activeFilter, setActiveFilter] = useState(FILTERS.ALL);
  const [searchTerm, setSerchTerm] = useState("");
  const checkUserPermissions = useCheckUserPermissions();

  const [formData, setFormData] = useState({
    id: user?.id || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    active: user?.active || false,
    admin: user?.admin || false,
    superUser: user?.superUser || false,
    apiTokenAllowed: user?.apiTokenAllowed || false,
    brands: user?.brands || [],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [generatedToken, setGeneratedToken] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((data) => ({
      ...data,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name } = e.target;
    setFormData((data) => ({
      ...data,
      [name]: !data[name],
    }));
  };

  const handleGenerateApiToken = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    const userId = formData.id;

    if (!userId) {
      alert("User ID is missing. Please save the user first.");
      return;
    }

    const confirmed = window.confirm("Are you sure you want to generate a new API token? This will invalidate any existing token.");

    if (confirmed) {
      try {
        const token = await dispatch(generateApiToken(userId));
        setGeneratedToken(token);
        setShowTokenModal(true);
      } catch (error) {
        alert("An error occurred while generating the API token. Please try again later.");
      }
    }
  };

  const handleCloseTokenModal = () => {
    setShowTokenModal(false);
    setGeneratedToken("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm(formData);
    setErrors(newErrors);

    // Remove id and admin from formData before sending to the server
    const { id, admin, ...formDataWithoutIdAndAdmin } = { ...formData };
    const payload = formDataWithoutIdAndAdmin;

    if (Object.keys(newErrors).length === 0) {
      try {
        if (creatingNewUser) {
          await dispatch(addUser(payload));
        } else {
          await dispatch(editUser(user.email, payload));
        }

        setUserModalOpened(false);
      } catch (error: any) {
        if (error?.response?.data?.message === "email already exists, please contact support@smartocto.com") {
          setErrors({ email: "Email already exists, please contact support@smartocto.com." });
        } else {
          setErrors({ root: "Unexpected error." });
        }
      }
    }
  };

  const validateForm = (data) => {
    const errors: FormErrors = {};

    if (!data.firstName.trim()) {
      errors.firstName = "First Name is required.";
    }

    if (!data.lastName.trim()) {
      errors.lastName = "Last Name is required.";
    }

    if (!data.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = "Email is invalid.";
    }

    return errors;
  };

  const handleSearch = (e) => {
    setSerchTerm(e.target.value);
  };

  const filteredBrands = useMemo(() => {
    if (brandsLoading) return [];
    const filtered = brands.filter((brand) => {
      return (
        brand.name.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 ||
        brand.id.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1
      );
    });
    return filtered.sort((a, b) => {
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    });
  }, [brands, searchTerm, brandsLoading]);

  const assignedBrands = useMemo(() => {
    if (brandsLoading) return [];
    let assigned = [] as Array<{ name: string; id: string }>;
    filteredBrands.forEach((brand) => {
      if (formData.brands.indexOf(brand.id) > -1) {
        assigned.push(brand);
      }
    });

    return assigned;
  }, [filteredBrands, formData.brands, brandsLoading]);

  const notAssignedBrands = useMemo(() => {
    if (brandsLoading) return [];
    let notAssigned = [] as Array<{ name: string; id: string }>;
    filteredBrands.forEach((brand) => {
      if (formData.brands.indexOf(brand.id) === -1) {
        notAssigned.push(brand);
      }
    });
    return notAssigned;
  }, [filteredBrands, formData.brands, brandsLoading]);

  const handleBrandToggle = (brandId) => {
    setFormData((data) => {
      const newBrands = [...data.brands];

      if (data.brands.includes(brandId)) {
        const index = newBrands.indexOf(brandId);
        if (index !== -1) {
          newBrands.splice(index, 1);
        }
      } else {
        newBrands.push(brandId);
      }

      return {
        ...data,
        brands: newBrands,
      };
    });
  };

  useEffect(() => {
    dispatch(fetchBrands());
  }, [dispatch]);

  const currentlyDisplayedBrands = () => {
    if (activeFilter === FILTERS.ALL) {
      return filteredBrands.map((brand) => {
        return (
          <div className="brand-option" key={brand.id}>
            <input
              type="checkbox"
              defaultChecked={formData.brands?.indexOf(brand.id) > -1 ? true : false}
              onChange={() => {
                handleBrandToggle(brand.id);
              }}
            />
            <b>{brand.id}</b> - <i>{brand.name}</i>
          </div>
        );
      });
    } else if (activeFilter === FILTERS.ASSIGNED) {
      return assignedBrands.map((brand) => {
        return (
          <div className="brand-option" key={brand.id}>
            <input
              type="checkbox"
              defaultChecked={true}
              onChange={() => {
                handleBrandToggle(brand.id);
              }}
            />
            <b>{brand.id}</b> - <i>{brand.name}</i>
          </div>
        );
      });
    } else if (activeFilter === FILTERS.NOT_ASSIGNED) {
      return notAssignedBrands.map((brand) => {
        return (
          <div className="brand-option" key={brand.id}>
            <input
              type="checkbox"
              defaultChecked={false}
              onChange={() => {
                handleBrandToggle(brand.id);
              }}
            />
            <b>{brand.id}</b> - <i>{brand.name}</i>
          </div>
        );
      });
    }
  };

  return (
    <div className="user-modal-wrapper">
      <form onSubmit={handleSubmit}>
        <h1 className="modal-title">{creatingNewUser ? "Add user" : "Edit user"}</h1>

        <div className="user-modal-container">
          <div className="user-modal-details">
            <Input
              id="active"
              label="User Active"
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleCheckboxChange}
            />
            <br />
            <br />
            <Input
              id="email"
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              separateLines
              fullWidth
              readOnly={creatingNewUser ? false : true}
              disabled={creatingNewUser ? false : true}
            />
            {errors.email && <div className="so-error">{errors.email}</div>}

            <Input
              id="firstName"
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              separateLines
              fullWidth
            />
            {errors.firstName && <div className="so-error">{errors.firstName}</div>}

            <Input
              id="lastName"
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              separateLines
              fullWidth
            />
            {errors.lastName && <div className="so-error">{errors.lastName}</div>}
            {checkUserPermissions("admin") && (
              <div className="api-token-section">
                <label>API Gateway Token</label>
                <p className="api-token-description">
                  Generate a new API Gateway Token for this user. The token will only be shown once.
                </p>
                <Button
                  variant="success"
                  className="generate-api-token-button"
                  onClick={(e) => {
                    handleGenerateApiToken(e);
                  }}
                  title="Generate a new API Gateway Token"
                >
                  <RefreshIcon /> Generate API Token
                </Button>
              </div>
            )}
            {errors.apiToken && <div className="so-error">{errors.apiToken}</div>}
            <Input
              id="isSuperUser"
              label="Super User"
              type="checkbox"
              name="superUser"
              checked={formData.superUser}
              onChange={handleCheckboxChange}
            />
            <br />
            <br />
            <Input
              id="apiTokenAllowed"
              label="API Gateway"
              type="checkbox"
              name="apiTokenAllowed"
              checked={formData.apiTokenAllowed}
              onChange={handleCheckboxChange}

            />
          </div>
          {brandsLoading ? (
            <div className="loader-container">
              <CircleLoader />
            </div>
          ) : (
            <div className="user-modal-brand-selector-container">
              <div className="user-modal-brand-selector-tabs">
                <div
                  className={[activeFilter === FILTERS.ALL && "active"].join()}
                  onClick={() => {
                    setActiveFilter(FILTERS.ALL);
                  }}
                >
                  All brands
                </div>
                <div
                  className={[activeFilter === FILTERS.ASSIGNED && "active"].join()}
                  onClick={() => {
                    setActiveFilter(FILTERS.ASSIGNED);
                  }}
                >
                  Assigned ({assignedBrands.length})
                </div>
                <div
                  className={[activeFilter === FILTERS.NOT_ASSIGNED && "active"].join()}
                  onClick={() => {
                    setActiveFilter(FILTERS.NOT_ASSIGNED);
                  }}
                >
                  Not Assigned
                </div>
              </div>
              <Input id="searchBrand" placeholder="Type to search brand" label="" onChange={handleSearch} fullWidth />
              <div className="user-modal-brand-selector">{currentlyDisplayedBrands()}</div>
            </div>
          )}
        </div>

        {showTokenModal && (
          <div className="token-modal-overlay" onClick={handleCloseTokenModal}>
            <div className="token-modal" onClick={(e) => e.stopPropagation()}>
              <div className="token-modal-header">
                <h3>API Token Generated</h3>
                <button className="token-modal-close" onClick={handleCloseTokenModal}>&times;</button>
              </div>
              <div className="token-modal-content">
                <p className="token-warning">
                  ⚠️ <strong>Important:</strong> This token will only be shown once. Please copy it now and store it securely.
                </p>
                <p className="token-warning-secondary">
                  If you forget this token, you will need to generate a new one.
                </p>
                <div className="token-display">
                  <code>{generatedToken}</code>
                </div>
                <Button
                  variant="default"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedToken);
                    alert("Token copied to clipboard!");
                  }}
                >
                  Copy to Clipboard
                </Button>
              </div>
              <div className="token-modal-footer">
                <Button variant="default" onClick={handleCloseTokenModal}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="user-modal-submit">
          {errors.root && <div className="so-error">{errors.root}</div>}
          <Button
            variant="success"
            rounded={false}
            type="submit"
            disabled={isPosting || brandsLoading}
            className="user-modal-save-btn"
          >
            {isPosting ? "Loading..." : "Save changes"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserFormModal;
